/**
 * Hosts that serve Firebase's `/__/auth/*` endpoints for themselves, and can
 * therefore be their own `authDomain`.
 *
 * Two kinds of host qualify, for two different reasons:
 *
 * - Firebase Hosting sites, which serve the auth handler automatically.
 * - Cloudflare Workers hosts, where `worker/index.ts` reverse-proxies
 *   `/__/auth/*` to Firebase Hosting.
 *
 * Anything absent from this list falls back to the build-time `authDomain`.
 * That matters most for local development: the Worker does not run under
 * `vite dev`, so nothing answers `/__/auth/handler` on `localhost` or on a
 * portless `*.trendaro.localhost` hostname.
 *
 * The list is deliberately explicit rather than a suffix match. A host only
 * belongs here once it is BOTH served by the proxy AND added to Firebase Auth's
 * authorized domains, which cannot be expressed as a pattern -- Firebase Auth
 * does not support wildcards. Per-PR preview hosts
 * (`pr-NNNN-trendaro-fe.behavio.workers.dev`) are therefore absent on purpose:
 * their names are minted per PR and can never be pre-authorized, so they keep
 * using the cross-domain flow. See issue #1091.
 */
const SELF_HOSTED_AUTH_DOMAINS: ReadonlySet<string> = new Set([
  // Firebase Hosting.
  "trendaro-v2-dev2.web.app",
  "trendaro-v2-stage.web.app",
  // Grouped under Firebase because that is what serves its /__/auth today. The
  // #1102 cutover hands that job to the Worker's proxy, which is the other
  // reason a host qualifies -- so membership here does not change, and neither
  // does this entry. The grouping is about what serves the host, not about
  // where the string lives.
  "moje.trendaro.cz",
  // Cloudflare Workers, via the /__/auth proxy.
  "dev.trendaro.cz",
  "moje2.trendaro.cz",
  "trendaro-fe.behavio.workers.dev",
  // One stage host per arm of the qset v3/v4 split. A preview alias resolves to
  // a single version, so the split needs two aliases and the old single `stage`
  // host no longer exists. Both are stable names, so both can be pre-authorized
  // in Firebase Auth -- unlike the per-PR hosts above.
  "stage-v3-trendaro-fe-production.behavio.workers.dev",
  "stage-v4-trendaro-fe-production.behavio.workers.dev",
]);

/**
 * Picks the `authDomain` for the host the app is running on.
 *
 * Using the current host keeps the OAuth redirect flow same-origin, which is
 * what makes `signInWithRedirect` survive browsers that block third-party
 * storage. Where that is not possible, the configured domain is used instead
 * and sign-in falls back to the cross-domain behaviour.
 *
 * @param hostname - Host the app is served from, typically `location.hostname`.
 * @param fallback - Configured `authDomain` for the current build.
 */
export const resolveAuthDomain = (
  hostname: string,
  fallback: string,
): string => (SELF_HOSTED_AUTH_DOMAINS.has(hostname) ? hostname : fallback);
